<template>
  <div class="animated">
    <div v-if="instances.length">
      <b-row>
        <b-col class="pr-0">
          <b-select :options="instance_options" v-model="selected_instance" />
        </b-col>
        <b-col>
          <b-select
            :options="analysis_options"
            :disabled="!selected_instance"
            v-model="selected_analysis"
          />
        </b-col>
      </b-row>
      <div v-if="selected_instance && selected_analysis">
        <div v-if="result_collection && result_collection.models.length">
          <schedule-results-table
            class="mt-3"
            :results="result_collection"
            :picked_analysis="picked_analysis"
          />
        </div>
        <b-alert v-else show class="m-0 mt-3">
          No samples for this instrument and analysis
        </b-alert>
      </div>
      <div v-else>
        <b-alert show class="m-0 mt-3"
          >Please select an instrument and analysis</b-alert
        >
      </div>
    </div>
    <div v-else>
      <b-alert show class="m-0"
        >No instruments for this task have been scheduled.</b-alert
      >
    </div>
  </div>
</template>

<script>
import AnalysisCollection from "@/collections/lab/AnalysisCollection";
import AnalyteResultCollection from "@/collections/lab/AnalyteResultCollection";
import InstrumentInstanceScheduleCollection from "@/collections/lab/InstrumentInstanceScheduleCollection";
import AnalyteResultModel from "@/models/lab/AnalyteResultModel";
import ScheduleResultsTable from "./ScheduleResultsTable";
import { getModel } from "@/shared/fetch-utils";

export default {
  name: "ScheduleTaskResults",
  components: { ScheduleResultsTable },
  props: {
    task: {
      type: Object,
      default: () => {
        return {};
      }
    },
    instances: {
      type: Array,
      default: () => []
    },
    samples: {
      type: Array,
      default: () => []
    },
    analyses: {
      type: AnalysisCollection,
      default: () => new AnalysisCollection()
    }
  },
  data() {
    return {
      result_collection: null,
      selected_instance: null,
      selected_analysis: null,
      picked_analysis: null
    };
  },
  computed: {
    instance_options() {
      const options = [
        {
          value: null,
          text: "Please select an instrument"
        },
        ...this.instances.map(({ id, name }) => {
          return {
            value: id,
            text: name
          };
        })
      ];

      this.instances.length === 1 &&
        (this.selected_instance = this.instances[0].id);

      return options;
    },
    analysis_options() {
      const options = [
        { value: null, text: "Please select an analysis" },
        ...this.analyses.models.map(({ id, name }) => {
          return {
            value: id,
            text: name
          };
        })
      ];

      this.analyses.models.length === 1 &&
        (this.selected_analysis = this.analyses.models[0].id);

      return options;
    },
    update_results() {
      this.populateTaskResults(this.selected_instance, this.selected_analysis);
      return this.selected_instance + this.selected_analysis;
    },
    current_sample_list() {
      return this.selected_instance
        ? this.samples.reduce((acc, cur) => {
            const check = cur.instrument_instance_id === this.selected_instance;

            check && acc.add(cur);

            return acc;
          }, new InstrumentInstanceScheduleCollection())
        : null;
    }
  },
  methods: {
    async fetchAnalysis() {
      this.picked_analysis = await getModel({
        model_type: "analysis",
        id: this.selected_analysis,
        message: this.$bvToast
      });
    },
    uddapteResults() {
      this.selected_instance &&
        this.selected_analysis &&
        this.populateTaskResults(
          this.selected_instance,
          this.selected_analysis
        );
    },
    populateTaskResults(instance_id, analysis_id) {
      if (!(analysis_id && instance_id)) {
        return (this.result_collection = null);
      }

      let results = new AnalyteResultCollection();
      let schedules = this.current_sample_list;

      if (
        this.task &&
        analysis_id &&
        instance_id &&
        schedules &&
        schedules.models.length
      ) {
        schedules.each(iis => {
          let analysis = null;
          let sample = iis.getSample();
          let instance = iis.getInstrumentInstance();

          if (this.task.id !== iis.schedulable_task_id) {
            return true;
          }

          this.analyses.each(model => {
            if (model.id === analysis_id) {
              analysis = model;
            }
          });

          if (analysis) {
            const analyte = analysis.getAnalyte();
            const result = new AnalyteResultModel({
              schedulable_task_type: iis.schedulable_task_type,
              schedulable_task_id: this.task.id,
              schedulable_task: this.task,
              sample_id: sample.id,
              sample: sample,
              instrument_instance_id: instance.id,
              instrument_instance: instance,
              analysis_id: analysis.id,
              analysis: analysis,
              analyte_id: analyte.id,
              analyte
            });

            analysis.on("relatedModelLoaded", event => {
              if (event.key === "analyte") {
                result.analyte_id = analyte.id;
              }
            });

            results.add(result);
          }
        });
      }

      this.result_collection = results;
    }
  },
  watch: {
    selected_instance() {
      this.uddapteResults();
    },
    selected_analysis: {
      handler(newValue, oldValue) {
        this.uddapteResults();
        newValue && newValue !== oldValue && this.fetchAnalysis();
      },
      immediate: true
    }
  }
};
</script>
